<template>
  <div v-if="isEnableTaia">
    <div
      class="button-placeholder"
      :class="{ taiaTooltip: showTaiaTooltip }"
    ></div>
  </div>
  <SearchBar :placeholder="placeholder" class="flex my-auto grow" />
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted } from "vue";
import SearchBar from "../molecule/search/searchBar.vue";

interface NavSearchBarProps {
  showTaiaTooltip?: boolean;
  placeholder?: string;
  taiaButtonSelector?: string;
  taiaIframeSelector?: string;
}

const {
  showTaiaTooltip = false,
  placeholder,
  /** Matches the floating button your script adds (id may be my-floating-button-0, etc.). */
  taiaButtonSelector = '#my-floating-button-0, button.custom-button[id^="my-floating-button"]',
  taiaIframeSelector = "iframe.taia_iframe",
} = defineProps<NavSearchBarProps>();

const userStore = useUserStore();
const iframeStore = useIframeStore();
const { isEnableTaia } = storeToRefs(iframeStore);

function queryFocusable(selector: string): HTMLElement | null {
  const el = document.querySelector(selector);
  if (!el || !(el instanceof HTMLElement)) return null;
  if (
    el.matches(
      "button, a[href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
    )
  ) {
    return el;
  }
  const inner = el.querySelector(
    "button, a[href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
  );
  return inner instanceof HTMLElement ? inner : null;
}

function getTaiaButton(): HTMLElement | null {
  return queryFocusable(taiaButtonSelector);
}

function getTaiaIframe(): HTMLIFrameElement | null {
  const el = document.querySelector(taiaIframeSelector);
  return el instanceof HTMLIFrameElement ? el : null;
}

function focusTaiaButton() {
  nextTick(() => {
    getTaiaButton()?.focus();
  });
}

function focusTaiaIframe() {
  nextTick(() => {
    const el = getTaiaIframe();
    if (!el) return;
    try {
      el.contentWindow?.focus();
    } catch {
      el.focus();
    }
  });
}

async function focusTaiaIframeWhenReady(maxWaitMs = 2500, stepMs = 50) {
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const iframe = getTaiaIframe();
    if (iframe) {
      try {
        iframe.contentWindow?.focus();
      } catch {
        iframe.focus();
      }
      return;
    }
    await new Promise((resolve) => window.setTimeout(resolve, stepMs));
  }
}

function isTaiaButtonTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(taiaButtonSelector) !== null;
}

function onDocumentKeydown(e: KeyboardEvent) {
  if (!isTaiaButtonTarget(e.target)) return;
  if (e.key === "Enter" || e.key === " ") {
    window.setTimeout(() => {
      void focusTaiaIframeWhenReady();
    }, 0);
  }
}

function onDocumentClick(e: MouseEvent) {
  if (!isTaiaButtonTarget(e.target)) return;
  window.setTimeout(() => {
    void focusTaiaIframeWhenReady();
  }, 0);
}

function attachIframeFocusHandlers() {
  const iframe = getTaiaIframe();
  if (!iframe) return;

  iframe.addEventListener("focus", () => {
    iframe.classList.add("taia-iframe--host-ring");
  });

  iframe.addEventListener("blur", () => {
    iframe.classList.remove("taia-iframe--host-ring");
  });
}

onMounted(() => {
  document.addEventListener("keydown", onDocumentKeydown, true);
  document.addEventListener("click", onDocumentClick, true);
   attachIframeFocusHandlers();
});

onUnmounted(() => {
  document.removeEventListener("keydown", onDocumentKeydown, true);
  document.removeEventListener("click", onDocumentClick, true);
});

defineExpose({
  focusTaiaButton,
  focusTaiaIframe: focusTaiaIframeWhenReady,
});
</script>
 <style>
iframe.taia_iframe:focus,
iframe.taia_iframe:focus-visible,
iframe.taia_iframe.taia-iframe--host-ring {
  outline: 2px solid #000000 !important;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px #000000;
}
</style>